import styled, { keyframes } from "styled-components";
import { colors } from "../../../styles/colors";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const ModalOverlay = styled.div<{ $elevated?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(18, 34, 67, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ $elevated }) => ($elevated ? 4000 : 2000)};
  backdrop-filter: blur(4px);
  animation: ${fadeIn} 0.3s ease;
`;

export const ModalContainer = styled.div`
  background-color: ${colors.lightGray};
  padding: 20px;
  border-radius: 10px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  text-align: center;
  position: relative;
  direction: rtl;
  animation: ${slideUp} 0.3s ease;

  .modal-title {
    font-family: "Noto Sans Hebrew", sans-serif;
    font-weight: 900;
    font-size: 24px;
    letter-spacing: -0.33px;
    text-align: center;
    margin-bottom: 25px;
    color: ${colors.darkBlue};
    line-height: 1.2;
  }

  .modal-message {
    font-size: 16px;
    color: ${colors.blueGray};
    margin-bottom: 30px;
  }

  .close-button {
    background-color: #004ef7;
    color: #ffffff;
    border: none;
    width: 129px;
    height: 30px;
    border-radius: 100px;
    font-family: "Noto Sans Hebrew", sans-serif;
    font-weight: 400;
    font-size: 12px;
    letter-spacing: -0.22px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    gap: 8px;

    .button-icon {
      width: 14px;
      height: 14px;
      filter: brightness(0) invert(1);

      &.loading {
        animation: ${spin} 1s linear infinite;
      }
    }

    &:hover {
      opacity: 0.9;
      transform: scale(1.02);
    }

    &:active {
      transform: scale(0.98);
    }
  }

  .options-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px 12px;
    margin-bottom: 30px;
    padding: 10px 0;
    justify-items: center;
  }

  .personal-answer-textarea {
    width: 100%;
    box-sizing: border-box;
    margin: 0 0 18px;
    padding: 12px 14px;
    min-height: 97px;
    border-radius: 10px;
    border: 1px solid #7e8aad33;
    background: #ffffff;
    font-family: "Noto Sans Hebrew", sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: ${colors.darkBlue};
    text-align: right;
    resize: none;
    outline: none;

    &::placeholder {
      font-family: "Noto Sans Hebrew", sans-serif;
      font-weight: 400;
      font-style: normal;
      font-size: 16px;
      line-height: 18px;
      letter-spacing: -0.22px;
      text-align: right;
      color: rgba(18, 34, 67, 0.55);
    }

    &:focus {
      border-color: rgba(0, 78, 247, 0.65);
      box-shadow: 0 0 0 3px rgba(0, 78, 247, 0.12);
    }
  }
`;

export const BackgroundCircle = styled.button<{ $background: string; $isSelected?: boolean; $isImage?: boolean }>`
  width: 54px;
  height: 54px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$background};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  .upload-icon {
    width: 23px;
    height: 21px;
    object-fit: contain;
  }

  /* Selection box shadow (only for white gap) */
  box-shadow: ${props => props.$isSelected ? '0 0 0 4px #FFFFFF' : 'none'};
  
  /* Additional shadow for non-image selection border */
  ${props => props.$isSelected && !props.$isImage && `
    box-shadow: 0 0 0 4px #FFFFFF, 0 0 0 6px #004EF7;
  `}
  
  /* Gradient border for image selection */
  &::after {
    content: '';
    position: absolute;
    top: -6px;
    left: -6px;
    right: -6px;
    bottom: -6px;
    border-radius: 50%;
    padding: 2px;
    background: linear-gradient(324.04deg, #00EAB2 4.81%, #00DEB7 15.03%, #00BFC4 33.12%, #008EDA 56.71%, #004EF7 83.45%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
            mask-composite: exclude;
    opacity: ${props => (props.$isSelected && props.$isImage) ? 1 : 0};
    transition: opacity 0.2s ease-in-out;
    pointer-events: none;
  }

  &:hover {
    transform: scale(1.1);
    box-shadow: ${props => props.$isSelected 
      ? '0 0 0 4px #FFFFFF, 0 0 0 6px #004EF7, 0 4px 12px rgba(0, 0, 0, 0.1)' 
      : '0 4px 12px rgba(0, 0, 0, 0.1)'};
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${colors.lightBlueGrayBackground};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.blueGray};
  transition: all 0.2s ease;
  line-height: 0;
  z-index: 1;

  &:hover {
    background: ${colors.lightBlueGrayAlpha};
    color: ${colors.darkBlue};
    transform: scale(1.05);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;
